import { useState, type FormEvent } from 'react';
import { ExternalLink, Pencil, Plus, Receipt, Trash2 } from 'lucide-react';
import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { Card } from '@/components/card';
import { Dialog } from '@/components/dialog';
import { EmptyState } from '@/components/empty-state';
import { FileDrop } from '@/components/file-drop';
import { IconButton } from '@/components/icon-button';
import { Input } from '@/components/input';
import { Menu } from '@/components/menu';
import { Pager } from '@/components/pager';
import { Select } from '@/components/select';
import { Textarea } from '@/components/textarea';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { useFormError } from '@/lib/form-error';
import { centsToDollarsInput, dollars, dollarsToCents, formatDateLong } from '@/lib/format';
import {
  useCreateExpense,
  useDeleteExpense,
  useExpenses,
  useUpdateExpense,
  type ExpenseInput,
} from '@/lib/expenses';
import type { ExpenseData } from '@/types/api';

const CATEGORY_LABEL: Record<string, string> = {
  software: 'Software',
  hardware: 'Hardware',
  hosting: 'Hosting',
  travel: 'Travel',
  subcontractor: 'Subcontractor',
  supplies: 'Supplies',
  licensing: 'Licensing',
  other: 'Other',
};

export function ClientExpenses({ clientId }: { clientId: number }) {
  const [page, setPage] = useState(1);
  const { data: paginated, isLoading } = useExpenses(clientId, page);
  const list = paginated?.data ?? [];
  const meta = paginated?.meta;
  const [createOpen, setCreateOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const editing = editingId != null ? list.find((e) => e.id === editingId) ?? null : null;

  const unbilled = list
    .filter((e) => e.billable && !e.billed && e.billed_on_invoice_id == null)
    .reduce((sum, e) => sum + e.amount_cents, 0);

  return (
    <div className="space-y-4">
      <Card className="overflow-hidden">
        <div className="grid grid-cols-2 divide-x divide-[var(--color-border)] text-center">
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
              Unbilled billable
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">{dollars(unbilled)}</div>
          </div>
          <div className="px-4 py-3">
            <div className="text-xs uppercase tracking-wide text-[var(--color-foreground-subtle)]">
              Total logged
            </div>
            <div className="mt-0.5 text-lg font-semibold tabular-nums">
              {meta?.total ?? list.length}
            </div>
          </div>
        </div>
      </Card>

      <div className="flex justify-end">
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="size-4" /> Log expense
        </Button>
      </div>

      {isLoading ? (
        <p className="text-sm text-[var(--color-foreground-muted)]">Loading…</p>
      ) : list.length === 0 ? (
        <EmptyState
          icon={Receipt}
          title="No expenses yet"
          description="Log costs you incur for this client, mark them billable, and pull them onto an invoice."
          action={
            <Button onClick={() => setCreateOpen(true)}>
              <Plus className="size-4" /> Log expense
            </Button>
          }
        />
      ) : (
        <>
          <Card className="divide-y divide-[var(--color-border)] overflow-hidden">
            {list.map((expense) => (
              <ExpenseRow
                key={expense.id}
                expense={expense}
                clientId={clientId}
                onEdit={() => setEditingId(expense.id)}
              />
            ))}
          </Card>
          {meta ? (
            <Pager
              page={meta.current_page}
              lastPage={meta.last_page}
              total={meta.total}
              from={meta.from}
              to={meta.to}
              onChange={setPage}
            />
          ) : null}
        </>
      )}

      <ExpenseDialog clientId={clientId} open={createOpen} onClose={() => setCreateOpen(false)} />
      {editing ? (
        <ExpenseDialog clientId={clientId} expense={editing} onClose={() => setEditingId(null)} />
      ) : null}
    </div>
  );
}

function ExpenseRow({
  expense,
  clientId,
  onEdit,
}: {
  expense: ExpenseData;
  clientId: number;
  onEdit: () => void;
}) {
  const del = useDeleteExpense(clientId);
  const confirm = useConfirm();
  const toast = useToast();

  return (
    <div className="flex items-center justify-between gap-3 px-4 py-3">
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate font-medium">{expense.vendor || expense.description}</span>
          {expense.category ? (
            <Badge tone="neutral">{CATEGORY_LABEL[expense.category] ?? expense.category}</Badge>
          ) : null}
          {expense.billable ? (
            expense.billed_on_invoice_id != null ? (
              <Badge tone="info">On invoice</Badge>
            ) : (
              <Badge tone="success">Billable</Badge>
            )
          ) : (
            <Badge tone="neutral">Non-billable</Badge>
          )}
        </div>
        <div className="mt-0.5 truncate text-xs text-[var(--color-foreground-subtle)]">
          {expense.vendor ? `${expense.description} · ` : ''}
          {formatDateLong(expense.spent_on)}
        </div>
      </div>

      <div className="text-right font-semibold tabular-nums">{dollars(expense.amount_cents)}</div>

      {expense.receipt_url ? (
        <IconButton
          onClick={() => window.open(expense.receipt_url!, '_blank')}
          aria-label="Open receipt"
        >
          <ExternalLink className="size-4" />
        </IconButton>
      ) : null}

      <Menu
        items={[
          { key: 'edit', label: 'Edit', icon: Pencil, onSelect: onEdit },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            danger: true,
            onSelect: async () => {
              const ok = await confirm({
                title: 'Delete expense?',
                body: 'This expense will be removed. This cannot be undone from the app.',
                confirmLabel: 'Delete',
                tone: 'danger',
              });
              if (ok) {
                await del.mutateAsync(expense.id);
                toast.success('Expense deleted');
              }
            },
          },
        ]}
      />
    </div>
  );
}

function ExpenseDialog({
  clientId,
  expense,
  open,
  onClose,
}: {
  clientId: number;
  expense?: ExpenseData;
  open?: boolean;
  onClose: () => void;
}) {
  const isEdit = !!expense;
  const isOpen = isEdit ? expense != null : !!open;
  const create = useCreateExpense(clientId);
  const update = useUpdateExpense(clientId);
  const toast = useToast();
  const { error, wrap } = useFormError();

  const today = new Date().toISOString().slice(0, 10);
  const [vendor, setVendor] = useState(expense?.vendor ?? '');
  const [description, setDescription] = useState(expense?.description ?? '');
  const [amount, setAmount] = useState(expense ? centsToDollarsInput(expense.amount_cents) : '');
  const [spentOn, setSpentOn] = useState(expense?.spent_on ?? today);
  const [category, setCategory] = useState(expense?.category ?? 'other');
  const [billable, setBillable] = useState(expense?.billable ?? true);
  const [receipt, setReceipt] = useState<File | null>(null);

  async function submit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const input: ExpenseInput = {
      vendor: vendor || undefined,
      description,
      amount_cents: dollarsToCents(amount) ?? 0,
      spent_on: spentOn,
      category,
      billable,
      receipt,
    };
    const ok = await wrap(() =>
      expense
        ? update.mutateAsync({ id: expense.id, input })
        : create.mutateAsync(input),
    );
    if (ok) {
      toast.success(expense ? 'Expense updated' : 'Expense logged');
      onClose();
    }
  }

  const pending = create.isPending || update.isPending;

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit expense' : 'Log expense'}
      size="lg"
    >
      <form className="space-y-4" onSubmit={submit}>
        <div className="grid grid-cols-2 gap-3">
          <label className="space-y-1 text-xs">
            Vendor (optional)
            <Input value={vendor} onChange={(e) => setVendor(e.target.value)} />
          </label>
          <label className="space-y-1 text-xs">
            Date
            <Input type="date" value={spentOn} onChange={(e) => setSpentOn(e.target.value)} required />
          </label>
          <label className="col-span-2 space-y-1 text-xs">
            Description
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              required
            />
          </label>
          <label className="space-y-1 text-xs">
            Amount ($)
            <Input
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </label>
          <label className="space-y-1 text-xs">
            Category
            <Select value={category} onChange={(e) => setCategory(e.target.value)}>
              {Object.entries(CATEGORY_LABEL).map(([v, l]) => (
                <option key={v} value={v}>
                  {l}
                </option>
              ))}
            </Select>
          </label>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={billable}
            onChange={(e) => setBillable(e.target.checked)}
            className="size-4 accent-[var(--color-primary)]"
          />
          <span>Billable to this client</span>
        </label>

        <div className="space-y-1 text-xs">
          <span className="text-[var(--color-foreground-muted)]">
            Receipt {isEdit ? '(upload to replace)' : '(optional)'}
          </span>
          <FileDrop file={receipt} onChange={setReceipt} />
        </div>

        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}

        <div className="flex justify-end gap-2 pt-1">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={pending}>
            {pending ? 'Saving…' : isEdit ? 'Save changes' : 'Log expense'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
